import { formatCurrency, formatDate, formatPercentage } from '@/lib/format'
import type {
  ScorecardActualDto,
  ScorecardKpiDto,
  ScorecardKpiCheckpointDto,
  ScorecardMetricDataType,
  ScorecardPeriodType,
} from '@/services/scorecard.service'

export type ScorecardChartDatum = {
  periodEndDate: string
  label: string
  axisLabel: string
  actual: number | null
  target: number | null
  isCheckpoint: boolean
  checkpointLabel: string | null
}

export type ScorecardSeriesPoint = {
  quarter: string
  periodEndDate: string
  value: number | null
}

type ScorecardChartKpi = {
  targetValue: number
  startValue?: number | null
  startDate: string
  targetDate: string
  direction?: 'HIGHER_IS_BETTER' | 'LOWER_IS_BETTER'
  checkpoints?: ScorecardKpiCheckpointDto[]
  actual?: Pick<ScorecardActualDto, 'value'> | null
}

export type ScorecardChartView = 'MONTHLY' | 'QUARTERLY' | 'ANNUAL'
export type ScorecardChartRange = 'GOAL_PERIOD' | 'FULL_HISTORY'
export type ScorecardGoalProgress = {
  startValue: number
  currentValue: number
  targetValue: number
  progressValue: number
  goalSpan: number
  remainingValue: number
  percent: number
}

type ScorecardTargetPathPoint = {
  checkpointDate: string
  value: number
  label: string | null
  priority: number
}

function parseDate(value: string | Date | null | undefined) {
  if (!value) return null
  const date = typeof value === 'string' ? new Date(value) : value
  return Number.isNaN(date.getTime()) ? null : date
}

function getFiscalYearEndMonth(fiscalYearEnd?: string | null) {
  if (!fiscalYearEnd) return null

  const isoLikeMatch = fiscalYearEnd.match(/^\d{4}-(\d{2})-\d{2}$/)
  if (isoLikeMatch) return Number(isoLikeMatch[1]) - 1

  const monthDayMatch = fiscalYearEnd.match(/^(\d{1,2})-\d{1,2}$/)
  if (monthDayMatch) return Number(monthDayMatch[1]) - 1

  const date = parseDate(fiscalYearEnd)
  return date ? date.getUTCMonth() : null
}

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10)
}

function getMonthBucketEnd(value: string) {
  const date = toValidDate(value)
  if (!date) return value

  return toIsoDate(
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0))
  )
}

function getQuarterBucketEnd(value: string, fiscalYearEnd?: string | null) {
  const date = toValidDate(value)
  if (!date) return value

  const month = date.getUTCMonth()
  const fiscalYearEndMonth = getFiscalYearEndMonth(fiscalYearEnd)

  if (fiscalYearEndMonth === null) {
    return toIsoDate(
      new Date(
        Date.UTC(date.getUTCFullYear(), Math.floor(month / 3) * 3 + 3, 0)
      )
    )
  }

  const fiscalYearStartMonth = (fiscalYearEndMonth + 1) % 12
  const fiscalMonthIndex = (month - fiscalYearStartMonth + 12) % 12
  const fiscalQuarterEndOffset = Math.floor(fiscalMonthIndex / 3) * 3 + 2
  const quarterEndMonth = (fiscalYearStartMonth + fiscalQuarterEndOffset) % 12
  const monthsUntilQuarterEnd = (quarterEndMonth - month + 12) % 12

  return toIsoDate(
    new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth() + monthsUntilQuarterEnd + 1,
        0
      )
    )
  )
}

function getNextMonthBucketEnd(value: string) {
  const date = toValidDate(value)
  if (!date) return null

  return toIsoDate(
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 2, 0))
  )
}

function getNextQuarterBucketEnd(value: string, fiscalYearEnd?: string | null) {
  const date = toValidDate(value)
  if (!date) return null

  const nextDay = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1)
  )

  return getQuarterBucketEnd(toIsoDate(nextDay), fiscalYearEnd)
}

function getChartBucketEnd(
  value: string,
  view: ScorecardChartView | undefined,
  fiscalYearEnd?: string | null
) {
  if (view === 'MONTHLY') return getMonthBucketEnd(value)
  if (view === 'QUARTERLY') return getQuarterBucketEnd(value, fiscalYearEnd)

  return value
}

function formatQuarterLabel(
  periodEndDate: string,
  fiscalYearEnd?: string | null
) {
  const date = parseDate(periodEndDate)
  if (!date) return null

  const fiscalYearEndMonth = getFiscalYearEndMonth(fiscalYearEnd)
  const month = date.getUTCMonth()
  const year = date.getUTCFullYear()

  if (fiscalYearEndMonth === null) {
    const quarter = Math.floor(month / 3) + 1
    return `Q${quarter} ${String(year).slice(-2)}`
  }

  const fiscalYearStartMonth = (fiscalYearEndMonth + 1) % 12
  const fiscalMonthIndex = (month - fiscalYearStartMonth + 12) % 12
  const fiscalQuarter = Math.floor(fiscalMonthIndex / 3) + 1
  const fiscalYear = month <= fiscalYearEndMonth ? year : year + 1

  return `Q${fiscalQuarter} FY${String(fiscalYear).slice(-2)}`
}

function getFiscalYear(periodEndDate: string, fiscalYearEnd?: string | null) {
  const date = parseDate(periodEndDate)
  if (!date) return null

  const fiscalYearEndMonth = getFiscalYearEndMonth(fiscalYearEnd) ?? 11
  const month = date.getUTCMonth()
  const year = date.getUTCFullYear()

  return month <= fiscalYearEndMonth ? year : year + 1
}

export function formatScorecardFiscalYearLabel(
  periodEndDate: string,
  fiscalYearEnd?: string | null
) {
  const fiscalYear = getFiscalYear(periodEndDate, fiscalYearEnd)
  if (fiscalYear === null) return null

  return `FY${String(fiscalYear).slice(-2)}`
}

export function shouldShowAnnualScorecardView(kpi: {
  periodType: ScorecardPeriodType
}) {
  return kpi.periodType === 'ANNUAL'
}

function toValidDate(value: string | null | undefined) {
  if (!value) return null

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function filterPointsForRange(
  points: ScorecardSeriesPoint[],
  kpi: ScorecardChartKpi,
  range: ScorecardChartRange
) {
  if (range === 'FULL_HISTORY') return points

  const startDate = toValidDate(kpi.startDate)
  const targetDate = toValidDate(kpi.targetDate)
  if (!startDate || !targetDate) return points

  return points.filter((point) => {
    const pointDate = toValidDate(point.periodEndDate)
    if (!pointDate) return false
    return pointDate >= startDate && pointDate <= targetDate
  })
}

function buildExplicitTargetPoint(periodEndDate: string): ScorecardSeriesPoint {
  return {
    quarter: formatDate(periodEndDate),
    periodEndDate,
    value: null,
  }
}

function sortPointsByDate(points: ScorecardSeriesPoint[]) {
  return [...points].sort((left, right) => {
    const leftDate = toValidDate(left.periodEndDate)
    const rightDate = toValidDate(right.periodEndDate)
    const leftTime = leftDate?.getTime() ?? Number.NEGATIVE_INFINITY
    const rightTime = rightDate?.getTime() ?? Number.NEGATIVE_INFINITY

    if (leftTime === rightTime) return 0
    return leftTime - rightTime
  })
}

function getTargetBaselineValue(
  kpi: ScorecardChartKpi,
  points: ScorecardSeriesPoint[]
) {
  if (kpi.startValue !== null && Number.isFinite(kpi.startValue)) {
    return kpi.startValue ?? null
  }

  const startDate = toValidDate(kpi.startDate)
  const targetDate = toValidDate(kpi.targetDate)
  if (!startDate || !targetDate) return kpi.actual?.value ?? null

  const datedActuals = points
    .map((point) => ({
      point,
      date: toValidDate(point.periodEndDate),
    }))
    .filter(
      (entry): entry is { point: ScorecardSeriesPoint; date: Date } =>
        entry.date !== null && entry.point.value !== null
    )

  const firstActualInGoal = datedActuals.reduce<{
    point: ScorecardSeriesPoint
    date: Date
  } | null>((earliest, entry) => {
    if (entry.date < startDate || entry.date > targetDate) return earliest
    if (!earliest || entry.date < earliest.date) return entry
    return earliest
  }, null)
  if (firstActualInGoal) return firstActualInGoal.point.value

  const latestActualBeforeGoal =
    datedActuals.reduce<ScorecardSeriesPoint | null>((latest, entry) => {
      if (entry.date >= startDate) return latest
      if (!latest) return entry.point

      const latestDate = toValidDate(latest.periodEndDate)
      if (!latestDate || entry.date > latestDate) return entry.point
      return latest
    }, null)

  return latestActualBeforeGoal?.value ?? kpi.actual?.value ?? null
}

function buildTargetPathPoints(
  kpi: ScorecardChartKpi,
  baselineValue: number | null
): ScorecardTargetPathPoint[] {
  if (baselineValue === null) return []

  const startDate = toValidDate(kpi.startDate)
  const targetDate = toValidDate(kpi.targetDate)

  if (!startDate || !targetDate) {
    return []
  }

  const checkpointPoints = (kpi.checkpoints ?? [])
    .filter((checkpoint) => {
      const checkpointDate = toValidDate(checkpoint.checkpointDate)
      return (
        checkpointDate !== null &&
        checkpointDate > startDate &&
        checkpointDate < targetDate
      )
    })
    .map((checkpoint) => ({
      checkpointDate: checkpoint.checkpointDate,
      value: checkpoint.value,
      label: checkpoint.label ?? null,
      priority: 1,
    }))

  const targetPoints = [
    {
      checkpointDate: kpi.startDate,
      value: baselineValue,
      label: null,
      priority: 0,
    },
    ...checkpointPoints,
    {
      checkpointDate: kpi.targetDate,
      value: kpi.targetValue,
      label: null,
      priority: 2,
    },
  ]

  const deduped = new Map<
    string,
    {
      checkpointDate: string
      value: number
      label: string | null
      priority: number
    }
  >()

  targetPoints
    .sort((left, right) => {
      const dateComparison = left.checkpointDate.localeCompare(
        right.checkpointDate
      )
      if (dateComparison !== 0) return dateComparison
      return left.priority - right.priority
    })
    .forEach((targetPoint) => {
      deduped.set(targetPoint.checkpointDate, targetPoint)
    })

  return [...deduped.values()].sort((left, right) =>
    left.checkpointDate.localeCompare(right.checkpointDate)
  )
}

function getNextChartBucketEnd(
  value: string,
  view: ScorecardChartView | undefined,
  fiscalYearEnd?: string | null
) {
  if (view === 'MONTHLY') return getNextMonthBucketEnd(value)
  if (view === 'QUARTERLY') return getNextQuarterBucketEnd(value, fiscalYearEnd)

  return null
}

function buildTargetPaceDates(
  targetPath: ScorecardTargetPathPoint[],
  view: ScorecardChartView | undefined,
  fiscalYearEnd?: string | null
) {
  if (targetPath.length < 2) return []
  if (view !== 'MONTHLY' && view !== 'QUARTERLY') return []

  const firstDate = targetPath[0]?.checkpointDate
  const lastDate = targetPath.at(-1)?.checkpointDate
  if (!firstDate || !lastDate) return []

  const dates = new Set<string>()
  let currentDate = getChartBucketEnd(firstDate, view, fiscalYearEnd)

  while (currentDate && currentDate >= firstDate && currentDate <= lastDate) {
    dates.add(currentDate)

    const nextDate = getNextChartBucketEnd(currentDate, view, fiscalYearEnd)
    if (!nextDate || nextDate <= currentDate) break

    currentDate = nextDate
  }

  return [...dates].sort((left, right) => left.localeCompare(right))
}

function buildTargetValuesByDate(
  targetPath: ScorecardTargetPathPoint[],
  targetPaceDates: string[]
) {
  const targetDates = [
    ...new Set([
      ...targetPath.map((point) => point.checkpointDate),
      ...targetPaceDates,
    ]),
  ].sort((left, right) => left.localeCompare(right))
  const targetValuesByDate = new Map<string, number>()
  const controlPoints = targetPath
    .map((point) => ({
      ...point,
      index: targetDates.indexOf(point.checkpointDate),
    }))
    .filter((point) => point.index >= 0)
    .sort((left, right) => left.index - right.index)

  controlPoints.forEach((point) => {
    targetValuesByDate.set(point.checkpointDate, point.value)
  })

  for (let index = 0; index < controlPoints.length - 1; index += 1) {
    const left = controlPoints[index]
    const right = controlPoints[index + 1]
    const span = right.index - left.index
    if (span <= 0) continue

    for (
      let targetIndex = left.index + 1;
      targetIndex < right.index;
      targetIndex += 1
    ) {
      const date = targetDates[targetIndex]
      const ratio = (targetIndex - left.index) / span
      targetValuesByDate.set(
        date,
        left.value + (right.value - left.value) * ratio
      )
    }
  }

  return targetValuesByDate
}

function findFirstActualPointInTargetWindow(
  points: ScorecardSeriesPoint[],
  kpi: ScorecardChartKpi
) {
  const startDate = toValidDate(kpi.startDate)
  const targetDate = toValidDate(kpi.targetDate)
  if (!startDate || !targetDate) return null

  return points
    .map((point) => ({
      point,
      date: toValidDate(point.periodEndDate),
    }))
    .filter(
      (
        entry
      ): entry is {
        point: ScorecardSeriesPoint
        date: Date
      } =>
        entry.date !== null &&
        entry.point.value !== null &&
        entry.date >= startDate &&
        entry.date <= targetDate
    )
    .sort((left, right) => left.date.getTime() - right.date.getTime())[0]?.point
}

function alignBaselineTargetToFirstActualPeriod(
  targetPath: ScorecardTargetPathPoint[],
  points: ScorecardSeriesPoint[],
  kpi: ScorecardChartKpi
) {
  const baselinePoint = targetPath.find((point) => point.priority === 0)
  if (!baselinePoint) return targetPath

  const firstActualPoint = findFirstActualPointInTargetWindow(points, kpi)
  if (!firstActualPoint) return targetPath
  if (firstActualPoint.periodEndDate === baselinePoint.checkpointDate) {
    return targetPath
  }

  const actualPeriodAlreadyHasTarget = targetPath.some(
    (point) =>
      point.priority !== 0 &&
      point.checkpointDate === firstActualPoint.periodEndDate
  )
  if (actualPeriodAlreadyHasTarget) return targetPath

  return targetPath
    .map((point) =>
      point.priority === 0
        ? { ...point, checkpointDate: firstActualPoint.periodEndDate }
        : point
    )
    .sort((left, right) =>
      left.checkpointDate.localeCompare(right.checkpointDate)
    )
}

function alignTargetPathToChartBuckets(
  targetPath: ScorecardTargetPathPoint[],
  view: ScorecardChartView | undefined,
  fiscalYearEnd?: string | null
) {
  if (!view) return targetPath

  const deduped = new Map<string, ScorecardTargetPathPoint>()

  targetPath
    .map((point) => ({
      ...point,
      checkpointDate:
        point.priority !== 0
          ? getChartBucketEnd(point.checkpointDate, view, fiscalYearEnd)
          : point.checkpointDate,
    }))
    .sort((left, right) => {
      const dateComparison = left.checkpointDate.localeCompare(
        right.checkpointDate
      )
      if (dateComparison !== 0) return dateComparison
      return left.priority - right.priority
    })
    .forEach((point) => {
      deduped.set(point.checkpointDate, point)
    })

  return [...deduped.values()].sort((left, right) =>
    left.checkpointDate.localeCompare(right.checkpointDate)
  )
}

function mergeChartPoints(
  actualPoints: ScorecardSeriesPoint[],
  targetPath: ScorecardTargetPathPoint[],
  targetPaceDates: string[],
  shouldAppendTarget: boolean
) {
  if (!shouldAppendTarget || targetPath.length === 0) {
    return sortPointsByDate(actualPoints)
  }

  const merged = new Map<string, ScorecardSeriesPoint>()

  actualPoints.forEach((point) => {
    merged.set(point.periodEndDate, point)
  })

  const targetDates = [
    ...new Set([
      ...targetPath.map((targetPoint) => targetPoint.checkpointDate),
      ...targetPaceDates,
    ]),
  ]

  targetDates.forEach((targetDate) => {
    const existing = merged.get(targetDate)
    if (existing) return

    merged.set(targetDate, buildExplicitTargetPoint(targetDate))
  })

  return sortPointsByDate([...merged.values()])
}

function formatMonthLabel(periodEndDate: string) {
  const date = parseDate(periodEndDate)
  if (!date) return null

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
    year: '2-digit',
  }).format(date)
}

function formatChartLabel(
  point: ScorecardSeriesPoint,
  view: ScorecardChartView | undefined,
  fiscalYearEnd?: string | null
) {
  const fallbackLabel = point.quarter || formatDate(point.periodEndDate)

  if (view === 'QUARTERLY') {
    if (point.quarter !== formatDate(point.periodEndDate)) {
      return (
        formatQuarterLabel(point.periodEndDate, fiscalYearEnd) ?? fallbackLabel
      )
    }

    const isArtificialQuarterBucket =
      point.quarter === formatDate(point.periodEndDate) &&
      point.periodEndDate ===
        getQuarterBucketEnd(point.periodEndDate, fiscalYearEnd)

    if (isArtificialQuarterBucket) {
      return (
        formatQuarterLabel(point.periodEndDate, fiscalYearEnd) ?? fallbackLabel
      )
    }

    return fallbackLabel
  }

  if (view === 'ANNUAL') {
    return (
      formatScorecardFiscalYearLabel(point.periodEndDate, fiscalYearEnd) ??
      fallbackLabel
    )
  }

  if (
    view === 'MONTHLY' &&
    point.quarter === formatDate(point.periodEndDate) &&
    point.periodEndDate === getMonthBucketEnd(point.periodEndDate)
  ) {
    return formatMonthLabel(point.periodEndDate) ?? fallbackLabel
  }

  return fallbackLabel
}

export function buildScorecardChartData(
  kpi: ScorecardChartKpi,
  points: ScorecardSeriesPoint[],
  options: {
    appendTargetPoint?: boolean
    range?: ScorecardChartRange
    view?: ScorecardChartView
    fiscalYearEnd?: string | null
  } = {}
): ScorecardChartDatum[] {
  const shouldAppendTarget = options.appendTargetPoint ?? true
  const range = options.range ?? 'FULL_HISTORY'
  const rangePoints = filterPointsForRange(points, kpi, range)
  const baselineValue = getTargetBaselineValue(kpi, points)
  const targetPath = buildTargetPathPoints(kpi, baselineValue)
  const baselineAlignedTargetPath = alignBaselineTargetToFirstActualPeriod(
    targetPath,
    rangePoints,
    kpi
  )
  const chartTargetPath = alignTargetPathToChartBuckets(
    baselineAlignedTargetPath,
    options.view,
    options.fiscalYearEnd
  )
  const targetPaceDates =
    shouldAppendTarget && range === 'GOAL_PERIOD'
      ? buildTargetPaceDates(
          chartTargetPath,
          options.view,
          options.fiscalYearEnd
        )
      : []
  const targetValuesByDate = buildTargetValuesByDate(
    chartTargetPath,
    targetPaceDates
  )
  const checkpointsByDate = new Map(
    chartTargetPath
      .filter((targetPoint) => targetPoint.priority === 1)
      .map((targetPoint) => [targetPoint.checkpointDate, targetPoint.label])
  )
  const sourcePoints = mergeChartPoints(
    rangePoints,
    chartTargetPath,
    targetPaceDates,
    shouldAppendTarget
  )

  if (sourcePoints.length === 0) return []

  return sourcePoints.map((point) => {
    const label = formatChartLabel(point, options.view, options.fiscalYearEnd)
    const isArtificialBaselinePoint =
      point.value === null &&
      chartTargetPath.some(
        (targetPoint) =>
          targetPoint.checkpointDate === point.periodEndDate &&
          targetPoint.priority === 0
      )
    const checkpointLabel = checkpointsByDate.get(point.periodEndDate)

    return {
      periodEndDate: point.periodEndDate,
      label,
      axisLabel: isArtificialBaselinePoint ? '' : label,
      actual: point.value,
      target: targetValuesByDate.get(point.periodEndDate) ?? null,
      isCheckpoint: checkpointLabel !== undefined,
      checkpointLabel: checkpointLabel ?? null,
    }
  })
}

export function buildScorecardStartValue(
  kpi: ScorecardChartKpi,
  points: ScorecardSeriesPoint[]
) {
  return getTargetBaselineValue(kpi, points)
}

export function buildScorecardGoalProgress(
  kpi: ScorecardChartKpi,
  points: ScorecardSeriesPoint[]
): ScorecardGoalProgress | null {
  const startValue = getTargetBaselineValue(kpi, points)
  const currentValue = kpi.actual?.value ?? null
  if (startValue === null || currentValue === null) return null

  const isLowerBetter = kpi.direction === 'LOWER_IS_BETTER'
  const goalSpan = isLowerBetter
    ? startValue - kpi.targetValue
    : kpi.targetValue - startValue
  if (!Number.isFinite(goalSpan) || goalSpan <= 0) return null

  const progressValue = isLowerBetter
    ? startValue - currentValue
    : currentValue - startValue
  const remainingValue = isLowerBetter
    ? currentValue - kpi.targetValue
    : kpi.targetValue - currentValue

  return {
    startValue,
    currentValue,
    targetValue: kpi.targetValue,
    progressValue,
    goalSpan,
    remainingValue,
    percent: (progressValue / goalSpan) * 100,
  }
}

export function formatScorecardValue(
  value: number | null | undefined,
  dataType: ScorecardMetricDataType | undefined,
  unitLabel?: string | null
) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  const isNegative = value < 0
  const absValue = Math.abs(value)

  if (dataType === 'CURRENCY') return formatCurrency(value)

  if (dataType === 'PERCENTAGE') {
    const formatted = formatPercentage(absValue)
    return isNegative ? `(${formatted})` : formatted
  }

  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(absValue)

  const displayValue = isNegative ? `(${formatted})` : formatted
  return unitLabel?.trim()
    ? `${displayValue} ${unitLabel.trim()}`
    : displayValue
}

export function getScorecardDirectionLabel(
  kpi: Pick<ScorecardKpiDto, 'direction'>
) {
  return kpi.direction === 'LOWER_IS_BETTER' ? 'Decrease' : 'Increase'
}

type GoalChangeInput = Partial<
  Pick<
    ScorecardKpiDto,
    'goalChangePercent' | 'targetMode' | 'targetPercent' | 'direction'
  >
>

function getGoalChangePercent(kpi: GoalChangeInput) {
  if (kpi.goalChangePercent !== undefined) return kpi.goalChangePercent
  if (kpi.targetMode !== 'PERCENT_CHANGE' || kpi.targetPercent == null)
    return null
  return kpi.direction === 'LOWER_IS_BETTER'
    ? -kpi.targetPercent
    : kpi.targetPercent
}

export function formatScorecardGoalPercentage(kpi: GoalChangeInput) {
  const percent = getGoalChangePercent(kpi)
  if (percent == null) return null
  return `${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(Math.abs(percent))}%`
}

export function getScorecardGoalChangeLabel(kpi: GoalChangeInput) {
  const percent = getGoalChangePercent(kpi)
  if (percent == null) return null
  if (formatScorecardGoalPercentage(kpi) === '0%') return 'no change'
  return percent < 0 ? 'decrease' : 'increase'
}

export function formatScorecardGoal(
  kpi: Pick<ScorecardKpiDto, 'targetValue' | 'unitLabel'> & GoalChangeInput,
  dataType: ScorecardMetricDataType | undefined
) {
  const value = formatScorecardValue(kpi.targetValue, dataType, kpi.unitLabel)
  const percent = formatScorecardGoalPercentage(kpi)
  if (percent === null) return value

  const direction = getScorecardGoalChangeLabel(kpi)
  if (direction === 'no change') return value
  return `${value} (${percent} ${direction})`
}
